/**
 * Главный composable для управления системой справки.
 * Объединяет функционал туров, панели справки и контекстных подсказок.
 */
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useTour } from './useTour'
import { useHelpProgress } from './useHelpProgress'

// Импортируем конфигурации туров
import onboardingTour from '../tours/onboarding.tour'
import devicesTour from '../tours/devices.tour'
import dashboardTour from '../tours/dashboard.tour'
import geozonesTour from '../tours/geozones.tour'
import reportsTour from '../tours/reports.tour'
import maintenanceTour from '../tours/maintenance.tour'
import driversTour from '../tours/drivers.tour'
import trailersTour from '../tours/trailers.tour'
import regtechTour from '../tours/regtech.tour'
import subusersTour from '../tours/subusers.tour'
import sharingTour from '../tours/sharing.tour'
import billingTour from '../tours/billing.tour'
import notificationsTour from '../tours/notifications.tour'
import requestsTour from '../tours/requests.tour'

// Импортируем контент справки страниц
import devicesHelp from '../content/pages/devices'
import dashboardHelp from '../content/pages/dashboard'
import geozonesHelp from '../content/pages/geozones'
import reportsHelp from '../content/pages/reports'
import maintenanceHelp from '../content/pages/maintenance'
import driversHelp from '../content/pages/drivers'
import trailersHelp from '../content/pages/trailers'
import regtechHelp from '../content/pages/regtech'
import subusersHelp from '../content/pages/subusers'
import sharingHelp from '../content/pages/sharing'
import billingHelp from '../content/pages/billing'
import notificationsHelp from '../content/pages/notifications'
import requestsHelp from '../content/pages/requests'

// Карта туров по страницам
const pageTours = {
  devices: devicesTour,
  dashboard: dashboardTour,
  geozones: geozonesTour,
  reports: reportsTour,
  maintenance: maintenanceTour,
  drivers: driversTour,
  trailers: trailersTour,
  regtech: regtechTour,
  subusers: subusersTour,
  sharing: sharingTour,
  billing: billingTour,
  notifications: notificationsTour,
  requests: requestsTour,
}

// Карта контента справки по страницам
const pageHelpContent = {
  devices: devicesHelp,
  dashboard: dashboardHelp,
  geozones: geozonesHelp,
  reports: reportsHelp,
  maintenance: maintenanceHelp,
  drivers: driversHelp,
  trailers: trailersHelp,
  regtech: regtechHelp,
  subusers: subusersHelp,
  sharing: sharingHelp,
  billing: billingHelp,
  notifications: notificationsHelp,
  requests: requestsHelp,
}

// Состояние панели справки (синглтон)
const isPanelOpen = ref(false)

export function useHelp() {
  const route = useRoute()
  const { startTour, stopTour, isRunning: isTourRunning } = useTour()
  const {
    shouldShowOnboarding,
    completeOnboarding,
    resetProgress,
    resetTooltips,
    isTourCompleted,
  } = useHelpProgress()

  // Определяем текущую страницу по маршруту
  const currentPage = computed(() => {
    const name = route.name
    if (!name) return null
    // Преобразуем имя маршрута в ключ страницы
    // 'devices' -> 'devices', 'device-details' -> 'devices'
    if (name.startsWith('device')) return 'devices'
    if (name.startsWith('geozone')) return 'geozones'
    if (name.startsWith('report')) return 'reports'
    if (name.startsWith('maintenance')) return 'maintenance'
    if (name.startsWith('billing')) return 'billing'
    if (name.startsWith('dashboard')) return 'dashboard'
    if (name.startsWith('driver')) return 'drivers'
    if (name.startsWith('trailer')) return 'trailers'
    if (name.startsWith('regtech')) return 'regtech'
    if (name.startsWith('subuser') || name.startsWith('sub-user')) return 'subusers'
    if (name === 'sharing') return 'sharing'
    if (name.startsWith('notification')) return 'notifications'
    if (name === 'requests') return 'requests'
    return name
  })

  // Контент справки для текущей страницы
  const currentPageHelp = computed(() => {
    if (!currentPage.value) return null
    return pageHelpContent[currentPage.value] || null
  })

  // Тур для текущей страницы
  const currentPageTour = computed(() => {
    if (!currentPage.value) return null
    return pageTours[currentPage.value] || null
  })

  // Есть ли тур для текущей страницы
  const hasPageTour = computed(() => !!currentPageTour.value)

  // Пройден ли тур текущей страницы
  const isPageTourCompleted = computed(() => {
    if (!currentPageTour.value) return true
    return isTourCompleted(currentPageTour.value.id)
  })

  /**
   * Открывает панель справки.
   */
  const openPanel = () => {
    isPanelOpen.value = true
  }

  /**
   * Закрывает панель справки.
   */
  const closePanel = () => {
    isPanelOpen.value = false
  }

  /**
   * Переключает состояние панели справки.
   */
  const togglePanel = () => {
    isPanelOpen.value = !isPanelOpen.value
  }

  /**
   * Запускает onboarding тур для новых пользователей.
   * @param {boolean} force - запустить принудительно
   */
  const runOnboarding = (force = false) => {
    if (force || shouldShowOnboarding()) {
      const started = startTour(onboardingTour, force)
      if (started) {
        // Onboarding будет отмечен как завершённый в useTour при закрытии
      }
    }
  }

  /**
   * Запускает тур по текущей странице.
   * @param {boolean} force - запустить даже если уже пройден
   */
  const runPageTour = (force = false) => {
    if (currentPageTour.value) {
      closePanel() // Закрываем панель перед туром
      startTour(currentPageTour.value, force)
    }
  }

  /**
   * Запускает конкретный тур по его идентификатору.
   * @param {string} tourId - идентификатор тура
   * @param {boolean} force - запустить даже если уже пройден
   */
  const runTourById = (tourId, force = false) => {
    const allTours = { onboarding: onboardingTour, ...pageTours }
    const tour = Object.values(allTours).find((t) => t.id === tourId)
    if (tour) {
      closePanel()
      startTour(tour, force)
    }
  }

  /**
   * Сбрасывает все туры и подсказки для повторного прохождения.
   */
  const resetAll = () => {
    resetProgress()
  }

  /**
   * Показывает все подсказки заново.
   */
  const showAllTooltips = () => {
    resetTooltips()
  }

  return {
    // Состояние
    isPanelOpen,
    isTourRunning,
    currentPage,
    currentPageHelp,
    hasPageTour,
    isPageTourCompleted,

    // Панель
    openPanel,
    closePanel,
    togglePanel,

    // Туры
    runOnboarding,
    runPageTour,
    runTourById,
    stopTour,

    // Сброс
    resetAll,
    showAllTooltips,
    shouldShowOnboarding,
    completeOnboarding,
  }
}
